import React, { Component, ErrorInfo, ReactNode } from 'react';
import { appInsights } from './AppInsights'; // Adjust path if your AppInsights.ts is elsewhere

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class AppErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    // 1. Update state to trigger the fallback UI on the next render
    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    // 2. Log the error details to Application Insights
    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        appInsights.trackException({
            error: error,
            properties: {
                context: 'ReactErrorBoundary',
                componentStack: errorInfo.componentStack || 'Unknown component stack',
                url: typeof window !== 'undefined' ? window.location.href : 'SSR/Unknown'
            }
        });

        // Optional: Keep console error for local debugging
        console.error('Uncaught React error caught by ErrorBoundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // Render your custom fallback UI, or use the one passed via props
            return (
                this.props.fallback || (
                    <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
                        <h2 style={{ color: '#d32f2f' }}>Something went wrong.</h2>
                        <p>The application encountered an unexpected error and has been logged.</p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '16px',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px'
                            }}
                        >
                            Reload Page
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{ marginTop: '20px', textAlign: 'left', color: '#666' }}>
                                <summary>Error Details (Dev Only)</summary>
                                <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto' }}>
                                    {this.state.error.toString()}
                                </pre>
                            </details>
                        )}
                    </div>
                )
            );
        }

        return this.props.children;
    }
}

export default AppErrorBoundary;